import React, { useState, useRef, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';

const DateTimePicker = ({ label, value, onChange, error, required, minDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('date'); // 'date' | 'time'
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [selectedHour, setSelectedHour] = useState(value ? new Date(value)?.getHours() % 12 || 12 : 12);
  const [selectedMinute, setSelectedMinute] = useState(value ? new Date(value)?.getMinutes() : 0);
  const [selectedPeriod, setSelectedPeriod] = useState(value ? (new Date(value)?.getHours() >= 12 ? 'PM' : 'AM') : 'AM');
  const containerRef = useRef(null);

  const effectiveMinDate = minDate ? startOfDay(new Date(minDate)) : startOfDay(new Date());

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInView = () => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  };

  const handleDateSelect = (day) => {
    if (isBefore(startOfDay(day), effectiveMinDate)) return;
    setSelectedDate(day);
    setCurrentMonth(day);
  };

  const handleConfirm = () => {
    if (!selectedDate) return;
    const result = new Date(selectedDate);
    let hours = selectedHour % 12;
    if (selectedPeriod === 'PM') hours += 12;
    result?.setHours(hours, selectedMinute, 0, 0);
    onChange(result);
    setIsOpen(false);
    setView('date');
  };

  const handleCancel = () => {
    setIsOpen(false);
    setView('date');
    if (value) {
      const d = new Date(value);
      setSelectedDate(d);
      setCurrentMonth(d);
      setSelectedHour(d?.getHours() % 12 || 12);
      setSelectedMinute(d?.getMinutes());
      setSelectedPeriod(d?.getHours() >= 12 ? 'PM' : 'AM');
    } else {
      setSelectedDate(null);
    }
  };

  const displayValue = value
    ? format(new Date(value), 'EEE, MMM d yyyy  hh:mm aa')
    : '';

  const headerDate = selectedDate ? format(selectedDate, 'MMM d') : '--';
  const headerYear = selectedDate ? format(selectedDate, 'yyyy') : new Date()?.getFullYear();
  const headerTime = `${String(selectedHour)?.padStart(2, '0')}:${String(selectedMinute)?.padStart(2, '0')}`;

  let hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
          {label}{required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      {/* Trigger Input */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-background border rounded-lg font-body text-sm md:text-base transition-all focus:outline-none focus:ring-2 focus:ring-ring ${
          error ? 'border-error focus:ring-error/30' : 'border-input hover:border-primary focus:border-transparent'
        }`}
      >
        <span className={displayValue ? 'text-foreground' : 'text-muted-foreground'}>
          {displayValue || 'Select date & time'}
        </span>
        <svg className="w-5 h-5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
      {/* Picker Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full max-w-xs rounded-xl shadow-2xl overflow-hidden" style={{ background: '#2d2d2d', minWidth: '300px' }}>
          {/* Header */}
          <div className="px-5 pt-5 pb-3" style={{ background: '#2d2d2d' }}>
            <p className="text-xs font-medium tracking-widest mb-2" style={{ color: '#9e9e9e' }}>SELECT DATE &amp; TIME</p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-sm mb-0.5" style={{ color: '#9e9e9e' }}>{headerYear}</p>
                <p className="text-4xl font-light" style={{ color: '#e0e0e0' }}>{headerDate}</p>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-4xl font-light" style={{ color: '#e0e0e0' }}>{headerTime}</p>
                <div className="flex flex-col text-sm font-medium" style={{ color: '#9e9e9e' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('AM')}
                    className={`leading-tight transition-colors ${ selectedPeriod === 'AM' ? 'text-white' : '' }`}
                    style={{ color: selectedPeriod === 'AM' ? '#e0e0e0' : '#9e9e9e' }}
                  >AM</button>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('PM')}
                    className="leading-tight transition-colors"
                    style={{ color: selectedPeriod === 'PM' ? '#e0e0e0' : '#9e9e9e' }}
                  >PM</button>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex border-b" style={{ borderColor: '#424242' }}>
            <button
              type="button"
              onClick={() => setView('date')}
              className="flex-1 flex items-center justify-center py-3 transition-colors"
              style={{ borderBottom: view === 'date' ? '2px solid #64b5f6' : '2px solid transparent' }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: view === 'date' ? '#64b5f6' : '#9e9e9e' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setView('time')}
              className="flex-1 flex items-center justify-center py-3 transition-colors"
              style={{ borderBottom: view === 'time' ? '2px solid #64b5f6' : '2px solid transparent' }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: view === 'time' ? '#64b5f6' : '#9e9e9e' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>

          {/* Calendar View */}
          {view === 'date' && (
            <div className="px-4 py-3">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-1 rounded transition-colors hover:bg-white/10"
                  style={{ color: '#e0e0e0' }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-medium" style={{ color: '#e0e0e0' }}>
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-1 rounded transition-colors hover:bg-white/10"
                  style={{ color: '#e0e0e0' }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 mb-1">
                {['S','M','T','W','T','F','S']?.map((d, i) => (
                  <div key={i} className="text-center text-xs font-medium py-1" style={{ color: '#9e9e9e' }}>{d}</div>
                ))}
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-7">
                {getDaysInView()?.map((day, idx) => {
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isDisabled = isBefore(startOfDay(day), effectiveMinDate);
                  const isTodayDate = isToday(day);

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleDateSelect(day)}
                      className="relative flex items-center justify-center h-8 w-full text-sm rounded-full transition-colors"
                      style={{
                        color: isDisabled ? '#555' : isSelected ? '#fff' : isCurrentMonth ? '#e0e0e0' : '#666',
                        background: isSelected ? '#64b5f6' : 'transparent',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        fontWeight: isTodayDate && !isSelected ? '600' : 'normal'
                      }}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Time View */}
          {view === 'time' && (
            <div className="px-4 py-4">
              <div className="flex items-center justify-center gap-4">
                {/* Hours */}
                <div className="flex flex-col items-center">
                  <p className="text-xs mb-2" style={{ color: '#9e9e9e' }}>Hour</p>
                  <div className="overflow-y-auto" style={{ maxHeight: '160px' }}>
                    {hours?.map(h => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setSelectedHour(h)}
                        className="flex items-center justify-center w-10 h-8 rounded-full text-sm transition-colors"
                        style={{
                          background: selectedHour === h ? '#64b5f6' : 'transparent',
                          color: selectedHour === h ? '#fff' : '#e0e0e0'
                        }}
                      >
                        {String(h)?.padStart(2, '0')}
                      </button>
                    ))}
                  </div>
                </div>

                <span className="text-2xl font-light" style={{ color: '#e0e0e0' }}>:</span>

                {/* Minutes */}
                <div className="flex flex-col items-center">
                  <p className="text-xs mb-2" style={{ color: '#9e9e9e' }}>Min</p>
                  <div className="overflow-y-auto" style={{ maxHeight: '160px' }}>
                    {minutes?.map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setSelectedMinute(m)}
                        className="flex items-center justify-center w-10 h-8 rounded-full text-sm transition-colors"
                        style={{
                          background: selectedMinute === m ? '#64b5f6' : 'transparent',
                          color: selectedMinute === m ? '#fff' : '#e0e0e0'
                        }}
                      >
                        {String(m)?.padStart(2, '0')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AM/PM */}
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('AM')}
                    className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                    style={{
                      background: selectedPeriod === 'AM' ? '#64b5f6' : 'transparent',
                      color: selectedPeriod === 'AM' ? '#fff' : '#9e9e9e'
                    }}
                  >AM</button>
                  <button
                    type="button"
                    onClick={() => setSelectedPeriod('PM')}
                    className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                    style={{
                      background: selectedPeriod === 'PM' ? '#64b5f6' : 'transparent',
                      color: selectedPeriod === 'PM' ? '#fff' : '#9e9e9e'
                    }}
                  >PM</button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 px-4 py-3" style={{ borderTop: '1px solid #424242' }}>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-1.5 text-sm font-medium tracking-wide transition-colors"
              style={{ color: '#64b5f6' }}
            >CANCEL</button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedDate}
              className="px-4 py-1.5 text-sm font-medium tracking-wide transition-colors"
              style={{ color: selectedDate ? '#64b5f6' : '#555' }}
            >OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
